import { useQuery } from '@tanstack/react-query';

/** Campaign data comes straight from the connected CRM's read-only campaign tools (no AI step). */
export interface Campaign {
  id: string;
  subject: string;
  channel: string;
  sentByName: string;
  createdAt: string;
  sentAt: string;
  recipients: number;
  delivered: number;
  failed: number;
  skipped: number;
  bounced: number;
  opened: number;
  openRate: number;
  replies: number;
}

export interface CampaignRecipient {
  deliveryId: string;
  playerId: string;
  name: string;
  address: string;
  status: 'Queued' | 'Sent' | 'Failed' | 'Bounced' | 'Skipped';
  error?: string | null;
  sentAt: string;
  openedAt: string;
  openCount: number;
  replied: boolean;
}

export interface CampaignDetail extends Campaign {
  body: string;
  recipientList: CampaignRecipient[];
}

export interface CampaignReply {
  inboxId: string;
  dtsMessageId: string;
  rfcMessageId?: string | null;
  campaignId: string;
  campaignSubject: string;
  fromName?: string | null;
  fromAddress: string;
  subject: string;
  snippet: string;
  receivedAt: string;
  playerId?: string | null;
  mailbox?: string | null;
}

async function call<T>(tool: string, args?: Record<string, unknown>): Promise<T> {
  const r = await window.dts.crm.call(tool, args);
  if (!r.ok) throw new Error(r.error);
  return r.data.data as T;
}

export const ck = {
  list: ['campaigns'] as const,
  one: (id: string) => ['campaign', id] as const,
  replies: (id?: string) => ['campaign-replies', id ?? '*'] as const,
};

const opts = { staleTime: 60_000, retry: false, refetchInterval: 5 * 60_000 } as const;

/** Errors (no CRM connected, or it has no campaigns feature) leave data undefined, which hides the sidebar folder. */
export function useCampaigns() {
  return useQuery({ queryKey: ck.list, queryFn: async () => (await call<{ campaigns: Campaign[] }>('list_campaigns', { limit: 50 })).campaigns, ...opts });
}
export function useCampaign(id?: string) {
  return useQuery({ queryKey: ck.one(id ?? ''), queryFn: () => call<CampaignDetail>('get_campaign', { campaignId: id }), enabled: !!id, ...opts });
}
export function useCampaignReplies(campaignId?: string) {
  return useQuery({ queryKey: ck.replies(campaignId), queryFn: async () => (await call<{ replies: CampaignReply[] }>('list_campaign_replies', { campaignId, limit: 100 })).replies, ...opts });
}
